import { useEffect, useState, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert,
  Tabs,
  Tab,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import DateRangeSelector from '../../../../components/DateRangeSelector';
import FilterSelectorWithApp from '../../../../components/FilterSelectorWithApp';
import AdcashAvatyeDetailDashboard from '../../../../components/adcash/avatye/detail/AdcashAvatyeDetailDashboard';
import AdcashAvatyeDetailSummaryTable from '../../../../components/adcash/avatye/detail/table/AdcashAvatyeDetailSummaryTable';
import AdcashAvatyeMediationTable from '../../../../components/adcash/avatye/detail/table/AdcashAvatyeMediationTable';
import AdcashAvatyePlacementTable from '../../../../components/adcash/avatye/detail/table/AdcashAvatyePlacementTable';
import AdcashAvatyeDetailMetricsTable from '../../../../components/adcash/avatye/detail/table/AdcashAvatyeDetailMetricsTable';
import apiClient from '../../../../utils/axiosClient';
import { PAGE_CARD_SX } from '../../../../theme/pageCardSx';
import dayjs, { Dayjs } from 'dayjs';
import { 
    AdcashAvatyeDetailMetrics,
    AdcashAvatyeDetailMetricsByMediation,
    AdcashAvatyeDetailMetricsByPlacement,
    AdcashAvatyeDetailMetricsSummary,
    AdcashAvatyeDetailRevenueSummary
} from '../../../../types/adcash.types';

export default function AdcashAvatyeDetail() {
  const [avatyeDetailMetrics, setAvatyeDetailMetrics] = useState<AdcashAvatyeDetailMetrics | null>(null);
  const [avatyeDetailMetricsSummary, setAvatyeDetailMetricsSummary] = useState<AdcashAvatyeDetailMetricsSummary | null>(null);
  const [avatyeDetailMetricsByMediation, setAvatyeDetailMetricsByMediation] = useState<AdcashAvatyeDetailMetricsByMediation | null>(null);
  const [avatyeDetailMetricsByPlacement, setAvatyeDetailMetricsByPlacement] = useState<AdcashAvatyeDetailMetricsByPlacement | null>(null);
  const [dailyRevenue, setDailyRevenue] = useState<AdcashAvatyeDetailRevenueSummary>(
    {
        dailyRevenue: []
    }
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().subtract(0, 'day'));
  const [initialized, setInitialized] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [exchangeRate, setExchangeRate] = useState<number>(1230);

  // date와 revenue 필드를 가진 데이터에서 날짜별 매출 추출
  const fetchDailyRevenue = useCallback((
    data: Array<{ date: string; revenue?: number; totalRevenue?: number }>
  ) => {
    setDailyRevenue({
      dailyRevenue: data.map(item => ({
        date: item.date,
        revenue: (item.revenue ?? item.totalRevenue ?? 0)
      }))
    });
  }, []); 

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/adcash/avatye/app/list`, {
        params: {
          start_date: startDate?.format('YYYY-MM-DD'),
          end_date: endDate?.format('YYYY-MM-DD')
        }
      })
      .then((response) => {
        setAppOptions(['앱 전체', ...response.data]);
      });
  }, [startDate, endDate]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    setInitialized(true);
  }, []);

  const fetchAdcashAvatyeDetailMetricsSummary = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
        : {})
    };

    apiClient.get(`/api/v1/stat/adcash/avatye/detail/metrics/summary`, {
      params: params
    }).then((response) => {
      setAvatyeDetailMetricsSummary(response.data);
      fetchDailyRevenue(response.data.dailyStats);
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });

  }, [startDate, endDate, selectedApp, fetchDailyRevenue]);
  
  const fetchAdcashAvatyeDetailMetricsByMediation = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
        : {})
    };

    apiClient.get(`/api/v1/stat/adcash/avatye/detail/metrics/mediation`, {
      params: params
    }).then((response) => {
      setAvatyeDetailMetricsByMediation(response.data);
      // mediationStats에서 날짜별 매출 추출
      const dailyRevenueData = response.data.mediationStats.reduce((acc: Record<string, number>, stat: { date: string; revenue: number }) => {
        if (!acc[stat.date]) {
          acc[stat.date] = 0;
        }
        acc[stat.date] += stat.revenue;
        return acc;
      }, {});
      fetchDailyRevenue(Object.entries(dailyRevenueData).map(([date, revenue]) => ({ date, revenue: revenue as number })));
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp, fetchDailyRevenue]);

  const fetchAdcashAvatyeDetailMetricsByPlacement = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
        : {})
    };

    apiClient.get(`/api/v1/stat/adcash/avatye/detail/metrics/placement`, {
      params: params
    }).then((response) => {
      setAvatyeDetailMetricsByPlacement(response.data);
      fetchDailyRevenue(response.data.dailyStats);
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp, fetchDailyRevenue]);

  const fetchAdcashAvatyeDetailMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
        : {})
    };

    apiClient.get(`/api/v1/stat/adcash/avatye/detail/metrics`, {
      params: params
    }).then((response) => {
      setAvatyeDetailMetrics(response.data);
      fetchDailyRevenue(response.data.dailyStats);
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp, fetchDailyRevenue]);

  useEffect(() => {
    if (initialized && startDate && endDate && selectedApp != null) {
      // 현재 탭에 맞는 데이터 로드
      if (activeTab === 0) {
        fetchAdcashAvatyeDetailMetricsSummary();
      } else if (activeTab === 1) {
        fetchAdcashAvatyeDetailMetricsByMediation();
      } else if (activeTab === 2) {
        fetchAdcashAvatyeDetailMetricsByPlacement();
      } else if (activeTab === 3) {
        fetchAdcashAvatyeDetailMetrics();
      }
    }
  }, [
    fetchAdcashAvatyeDetailMetricsSummary,
    fetchAdcashAvatyeDetailMetricsByMediation,  
    fetchAdcashAvatyeDetailMetricsByPlacement,
    fetchAdcashAvatyeDetailMetrics,
    initialized,
    startDate,
    endDate,
    selectedApp,
    activeTab
  ]);

  return (
    <Box
      sx={{
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        minHeight: '100vh'
      }}
    >
      <Box
        alignItems={'center'}
        display={'flex'}
      >
        <Box>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={() => {
              if (startDate && endDate) {
                // 현재 탭에 맞는 데이터 로드
                if (activeTab === 0) {
                  fetchAdcashAvatyeDetailMetricsSummary();
                } else if (activeTab === 1) {
                  fetchAdcashAvatyeDetailMetricsByMediation();
                } else if (activeTab === 2) {
                  fetchAdcashAvatyeDetailMetricsByPlacement();
                } else if (activeTab === 3) {
                  fetchAdcashAvatyeDetailMetrics();
                }
              }
            }}
          />
        </Box>
        <Box
          sx={{
            ml: 3
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(setApp) => {
              setSelectedApp(setApp);
            }}
            onSearch={() => {
              if (startDate && endDate) {
                // 현재 탭에 맞는 데이터 로드
                if (activeTab === 0) {
                  fetchAdcashAvatyeDetailMetricsSummary();
                } else if (activeTab === 1) {
                  fetchAdcashAvatyeDetailMetricsByMediation();
                } else if (activeTab === 2) {
                  fetchAdcashAvatyeDetailMetricsByPlacement();
                } else if (activeTab === 3) {
                  fetchAdcashAvatyeDetailMetrics();
                }
              }
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* 앱 선택 (Chip) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            {activeTab !== 2 && activeTab !== 3 && (
              <AdcashAvatyeDetailDashboard
                dailyRevenue={dailyRevenue.dailyRevenue}
                exchangeRate={exchangeRate}
              />
            )}

            <Box
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                width: '1600px',
                maxWidth: '100%',
                borderRadius: 3,
                p: 2,
                mt: 3
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => {
                  setActiveTab(newValue);
                  // 탭 변경 시 해당 탭의 데이터 로드
                  if (newValue === 0) {
                    fetchAdcashAvatyeDetailMetricsSummary();
                  } else if (newValue === 1) {
                    fetchAdcashAvatyeDetailMetricsByMediation();
                  } else if (newValue === 2) {
                    fetchAdcashAvatyeDetailMetricsByPlacement();
                  } else if (newValue === 3) {
                    fetchAdcashAvatyeDetailMetrics();
                  }
                }}
              >
                <Tab value={0} label="전체 요약" />
                <Tab value={1} label="미디에이션 요약" />
                <Tab value={2} label="지면별 요약" />
                <Tab value={3} label="상세 조회" />
              </Tabs>

              {activeTab === 0 && avatyeDetailMetricsSummary && (
                <AdcashAvatyeDetailSummaryTable
                  data={avatyeDetailMetricsSummary}
                  exchangeRate={exchangeRate}
                  onExchangeRateChange={setExchangeRate}
                />
              )}
              {activeTab === 1 && avatyeDetailMetricsByMediation && (
                <AdcashAvatyeMediationTable
                  data={avatyeDetailMetricsByMediation}
                  exchangeRate={exchangeRate}
                  onExchangeRateChange={setExchangeRate}
                />
              )}
              {activeTab === 2 && avatyeDetailMetricsByPlacement && (
                <AdcashAvatyePlacementTable
                  data={avatyeDetailMetricsByPlacement}
                  exchangeRate={exchangeRate}
                  onExchangeRateChange={setExchangeRate}
                />
              )}
              {activeTab === 3 && avatyeDetailMetrics && (
                <AdcashAvatyeDetailMetricsTable
                  data={avatyeDetailMetrics}
                  selectedApp={selectedApp}
                  exchangeRate={exchangeRate}
                  onExchangeRateChange={setExchangeRate}
                />
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
